/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import VendorMultiSelect, {
  components,
  ClearIndicatorProps,
  Props,
  ValueContainerProps,
  ControlProps,
  OptionProps,
} from 'react-select';
import isEmpty from 'lodash.isempty';
import { MultiselectOption } from '../../../types/multiselect';

const DEFAULT_MAX_ITEMS_TO_SHOW = 2;

function createValueContainer<T = unknown>(maxItemsToShow: number) {
  return function ValueContainer({
    children,
    selectProps,
    ...props
  }: ValueContainerProps<MultiselectOption<T>, true>) {
    const { value, placeholder } = selectProps;
    const valueArray = Array.isArray(value) ? value : null;
    if (valueArray && valueArray.length > maxItemsToShow) {
      return (
        <components.ValueContainer {...props} selectProps={selectProps}>
          {placeholder}
        </components.ValueContainer>
      );
    }
    return (
      <components.ValueContainer {...props} selectProps={selectProps}>
        {children}
      </components.ValueContainer>
    );
  };
}

function Control<T = unknown>({
  children,
  selectProps,
  ...props
}: ControlProps<MultiselectOption<T>, true>) {
  return (
    <components.Control {...props} selectProps={selectProps}>
      {children}
    </components.Control>
  );
}

function ClearIndicator<T = unknown>({
  children,
  selectProps,
  ...props
}: ClearIndicatorProps<MultiselectOption<T>, true>) {
  const { value } = selectProps;
  const valueArray = Array.isArray(value) ? value : null;
  const valueLength = valueArray?.length ?? 0;
  return (
    <div className="p-2 rounded-pill d-flex flex-row align-items-center bg-skyblue text-white ">
      <components.ClearIndicator {...props} selectProps={selectProps} className="text-white p-0" />
      <span className="fs-sm">
        {valueLength}
        {' '}
        elemento
        {valueLength > 1 ? 's' : ''}
      </span>
    </div>
  );
}

function Option<T = unknown>({
  children,
  isSelected,
  ...props
}: OptionProps<MultiselectOption<T>, true>) {
  return (
    <components.Option {...props} isSelected={isSelected}>
      <div className="d-flex align-items-center">
        {isSelected && (
          <span className="me-2 fs-xs">
            ✓
          </span>
        )}
        <span>{children}</span>
      </div>
    </components.Option>
  );
}

type MultiSelectProps<T = unknown> = Omit<Props<MultiselectOption<T>, true>, 'options' | 'isMulti' | 'onChange' | 'value'> & {
  options: MultiselectOption<T>[];
  onChange?: (selectedOptions: MultiselectOption<T>[]) => void;
  value?: MultiselectOption<T>[] | null;
  maxItemsToShow?: number;
};

function MultiSelect<T = unknown>({
  options, onChange, value, maxItemsToShow = DEFAULT_MAX_ITEMS_TO_SHOW, ...props
}: MultiSelectProps<T>) {
  const handleChange = (
    selected: readonly MultiselectOption<T>[] | null,
  ) => {
    if (onChange) {
      onChange(selected ? [...selected] : []);
    }
  };

  const selectValue = isEmpty(value) ? null : value;
  const ValueContainerComponent = createValueContainer<T>(maxItemsToShow);

  return (
    <VendorMultiSelect<MultiselectOption<T>, true>
      options={options}
      isMulti
      value={selectValue}
      onChange={handleChange}
      hideSelectedOptions={false}
      classNamePrefix="react-select"
      className="react-select-container"
      classNames={{
        control: () => (value?.length ? 'react-select__control--has-value' : ''),
      }}
      components={{
        IndicatorSeparator: null,
        ValueContainer: ValueContainerComponent,
        ClearIndicator,
        Control,
        Option,
      }}
      {...props}
    />
  );
}

export { MultiSelect };
